// components/admin/quotes/AdminQuotes.jsx
import { useState, useEffect, useCallback } from 'react';
import { quotesAPI } from '../../../api/quotes';
import Loader from '../../common/Loader';
import QuoteFilters from './QuoteFilters';
import QuotesTable from './QuotesTable';
import QuoteViewModal from './QuoteViewModal';
import QuoteFormModal from './QuoteFormModal';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import Notification from '../../common/Notification';
import { RefreshCw, Plus } from 'lucide-react';
import '../../../styles/AdminQuotes.css';

export default function AdminQuotes() {
  // États principaux
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // États pour les filtres
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  
  // États pour les modaux
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [activeModal, setActiveModal] = useState(null); // 'view', 'edit', 'create', 'delete'
  
  // État pour les notifications
  const [notification, setNotification] = useState(null);
  
  // Charger les devis
  const loadQuotes = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await quotesAPI.getAll(filters);
      
      // S'assurer que quotes est un tableau
      const quotesData = response?.data?.quotes || response?.data || [];
      
      if (!Array.isArray(quotesData)) {
        console.error('Les devis ne sont pas un tableau:', quotesData);
        setQuotes([]);
        setError('Format de données incorrect');
      } else {
        setQuotes(quotesData);
      }
      
    } catch (err) {
      console.error('Erreur lors du chargement des devis:', err);
      setError(err.message || 'Impossible de charger les devis');
      setQuotes([]);
      showNotification('error', 'Erreur lors du chargement des devis');
      
    } finally {
      setLoading(false);
    }
  }, [filters]);
  
  // Effet pour charger les devis
  useEffect(() => {
    loadQuotes();
  }, [loadQuotes]);
  
  // Afficher une notification
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification(null), 5000);
  };
  
  // Gestion des filtres
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };
  
  // Voir les détails d'un devis
  const handleViewQuote = (quote) => {
    setSelectedQuote(quote);
    setActiveModal('view');
  };
  
  // Éditer un devis
  const handleEditQuote = (quote) => {
    setSelectedQuote(quote);
    setActiveModal('edit');
  };
  
  // Créer un nouveau devis
  const handleCreateQuote = () => {
    setSelectedQuote(null);
    setActiveModal('create');
  };
  
  // Supprimer un devis
  const handleDeleteQuote = (quote) => {
    setSelectedQuote(quote);
    setActiveModal('delete');
  };
  
  // Envoyer un devis
  const handleSendQuote = async (id) => {
    if (!window.confirm('Envoyer ce devis au client ?')) return;
    
    try {
      await quotesAPI.send(id);
      showNotification('success', 'Devis envoyé avec succès');
      loadQuotes();
    } catch (err) {
      showNotification('error', 'Erreur lors de l\'envoi du devis');
    }
  };
  
  // Changer le statut
  const handleStatusChange = async (id, newStatus) => {
    try {
      await quotesAPI.updateStatus(id, newStatus);
      showNotification('success', 'Statut mis à jour');
      loadQuotes();
    } catch (err) {
      showNotification('error', 'Erreur lors de la mise à jour du statut');
    }
  };
  
  // Supprimer définitivement
  const handleConfirmDelete = async () => {
    if (!selectedQuote) return;
    
    try {
      await quotesAPI.delete(selectedQuote._id);
      showNotification('success', 'Devis supprimé avec succès');
      setActiveModal(null);
      loadQuotes();
    } catch (err) {
      showNotification('error', 'Erreur lors de la suppression');
    }
  };
  
  // Formater la date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };
  
  // Formater le prix
  const formatPrice = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };
  
  // Afficher le loader initial
  if (loading && quotes.length === 0) {
    return (
      <div className="admin-quotes">
        <div className="section-header">
          <h2>Gestion des Devis</h2>
        </div>
        <Loader />
      </div>
    );
  }
  
  return (
    <div className="admin-quotes">
      {/* Notifications */}
      {notification && (
        <Notification
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}
      
      {/* Header */}
      <div className="section-header">
        <div>
          <h2>Gestion des Devis</h2>
          <p className="subtitle">{quotes.length} devis trouvés</p>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-secondary"
            onClick={loadQuotes}
            disabled={loading}
          >
            <RefreshCw size={20} />
            Actualiser
          </button>
          
          <button 
            className="btn-primary"
            onClick={handleCreateQuote}
          >
            <Plus size={20} />
            Nouveau Devis
          </button>
        </div>
      </div>
      
      {/* Filtres */}
      <QuoteFilters
        filters={filters}
        onFilterChange={handleFilterChange}
        loading={loading}
      />
      
      {/* Tableau des devis */}
      <QuotesTable
        quotes={quotes}
        loading={loading}
        error={error}
        onView={handleViewQuote}
        onEdit={handleEditQuote}
        onDelete={handleDeleteQuote}
        onSend={handleSendQuote}
        formatDate={formatDate}
        formatPrice={formatPrice}
        onCreate={handleCreateQuote}
      />
      
      {/* Modals */}
      {activeModal === 'view' && selectedQuote && (
        <QuoteViewModal
          quote={selectedQuote}
          onClose={() => setActiveModal(null)}
          onEdit={() => {
            setActiveModal('edit');
          }}
          onSend={() => handleSendQuote(selectedQuote._id)}
          onStatusChange={(newStatus) => handleStatusChange(selectedQuote._id, newStatus)}
          formatDate={formatDate}
          formatPrice={formatPrice}
        />
      )}
      
      {activeModal === 'edit' && selectedQuote && (
        <QuoteFormModal
          mode="edit"
          quote={selectedQuote}
          onClose={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            loadQuotes();
            showNotification('success', 'Devis mis à jour avec succès');
          }}
          onError={(error) => showNotification('error', error)}
        />
      )}
      
      {activeModal === 'create' && (
        <QuoteFormModal
          mode="create"
          onClose={() => setActiveModal(null)}
          onSuccess={() => {
            setActiveModal(null);
            loadQuotes();
            showNotification('success', 'Devis créé avec succès');
          }}
          onError={(error) => showNotification('error', error)}
        />
      )}
      
      {activeModal === 'delete' && selectedQuote && (
        <DeleteConfirmationModal
          quote={selectedQuote}
          onClose={() => setActiveModal(null)}
          onConfirm={handleConfirmDelete}
        />
      )}
    </div>
  );
}